import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Badge,
  Table,
  Button,
  Spinner,
  Alert,
  Progress
} from 'reactstrap';
import {
  FaBoxes,
  FaCalendarAlt,
  FaWarehouse,
  FaHistory,
  FaChartLine,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimes
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { lotActions } from '../../sagas/lotSlice';
import { formatPrice } from '../../helpers/utility';

const LotDetails = ({ lot }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { mouvements, loadingMouvements } = useSelector(state => state.lots);

  useEffect(() => {
    if (lot?.numeroLot) {
      dispatch(lotActions.getHistoriqueMouvementsRequest({
        numeroLot: lot.numeroLot
      }));
    }
  }, [lot, dispatch]);

  const getStatutBadge = (statut) => {
    const statutConfig = {
      ACTIF: { color: 'success', icon: FaCheckCircle, text: t('lot.actif') },
      EPUISE: { color: 'warning', icon: FaExclamationTriangle, text: t('lot.epuise') },
      RETIRE: { color: 'secondary', icon: FaTimes, text: t('lot.retire') },
      NON_CONFORME: { color: 'danger', icon: FaExclamationTriangle, text: t('lot.nonConforme') }
    };

    const config = statutConfig[statut] || statutConfig.ACTIF;
    const IconComponent = config.icon;

    return (
      <Badge color={config.color} className="d-flex align-items-center">
        <IconComponent className="me-1" size={12} />
        {config.text}
      </Badge>
    );
  };

    const getExpirationStatus = (dateExpiration) => {
    if (!dateExpiration) return null;
    
    const today = new Date();
    const expDate = new Date(dateExpiration);
    const diffTime = expDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      return { 
        status: 'expired', 
        badge: <Badge color="danger">{t('lot.expired')}</Badge>,
        days: Math.abs(diffDays)
      };
    } else if (diffDays <= 7) {
      return { 
        status: 'critical', 
        badge: <Badge color="warning">{t('lot.expiringSoon', { days: diffDays })}</Badge>,
        days: diffDays
      };
    } else if (diffDays <= 30) {
      return { 
        status: 'warning', 
        badge: <Badge color="info">{t('lot.expiringIn', { days: diffDays })}</Badge>,
        days: diffDays
      };
    }
    return { 
      status: 'valid', 
      badge: <Badge color="success">{t('lot.valid')}</Badge>,
      days: diffDays
    };
  };

  const getMouvementTypeBadge = (type) => {
    const typeConfig = {
      ENTREE: { color: 'success', text: t('mouvement.entree') },
      SORTIE: { color: 'danger', text: t('mouvement.sortie') },
      TRANSFERT: { color: 'info', text: t('mouvement.transfert') },
      AJUSTEMENT: { color: 'warning', text: t('mouvement.ajustement') },
      INVENTAIRE: { color: 'primary', text: t('mouvement.inventaire') }
    };

    const config = typeConfig[type] || { color: 'secondary', text: type };
    return <Badge color={config.color}>{config.text}</Badge>;
  };

  const calculateUtilisationPercentage = () => {
    if (!lot.quantiteInitiale || lot.quantiteInitiale === 0) return 0;
    return ((lot.quantiteInitiale - lot.quantiteDisponible) / lot.quantiteInitiale) * 100;
  };

  const expirationInfo = getExpirationStatus(lot.dateExpiration);
  const utilisationPercentage = calculateUtilisationPercentage();

  return (
    <div className="lot-details">
      {/* Informations générales */}
      <Row className="mb-4">
        <Col md={6}>
          <Card className="h-100">
            <CardHeader className="bg-primary text-white">
              <h5 className="mb-0">
                <FaBoxes className="me-2" />
                {t('lot.informationsGenerales')}
              </h5>
            </CardHeader>
            <CardBody>
              <Row className="mb-3">
                <Col sm={4}>
                  <strong>{t('lot.numeroLot')}:</strong>
                </Col>
                <Col sm={8}>
                  <code className="bg-light p-1 rounded">{lot.numeroLot}</code>
                </Col>
              </Row>
              
              <Row className="mb-3">
                <Col sm={4}>
                  <strong>{t('lot.article')}:</strong>
                </Col>
                <Col sm={8}>
                  <div>
                    <strong>{lot.article?.nom || lot.articleNom}</strong>
                    <br />
                    <small className="text-muted">
                      {t('lot.code')}: {lot.article?.code || lot.articleCode}
                    </small>
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col sm={4}>
                  <strong>{t('lot.emplacement')}:</strong>
                </Col>
                <Col sm={8}>
                  <Badge color="info" className="p-2">
                    <FaWarehouse className="me-1" />
                    {lot.emplacement}
                  </Badge>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col sm={4}>
                  <strong>{t('lot.statut')}:</strong>
                </Col>
                <Col sm={8}>
                  {getStatutBadge(lot.statut)}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>

        <Col md={6}>
          <Card className="h-100">
            <CardHeader className="bg-info text-white">
              <h5 className="mb-0">
                <FaCalendarAlt className="me-2" />
                {t('lot.informationsDates')}
              </h5>
            </CardHeader>
            <CardBody>
              <Row className="mb-3">
                <Col sm={5}>
                  <strong>{t('lot.dateProduction')}:</strong>
                </Col>
                <Col sm={7}>
                  {lot.dateProduction ? 
                    new Date(lot.dateProduction).toLocaleDateString('fr-FR') : 
                    <span className="text-muted">-</span>
                  }
                </Col>
              </Row>

              <Row className="mb-3">
                <Col sm={5}>
                  <strong>{t('lot.dateExpiration')}:</strong>
                </Col>
                <Col sm={7}>
                  {lot.dateExpiration ? (
                    <div>
                      {new Date(lot.dateExpiration).toLocaleDateString('fr-FR')}
                      <br />
                      {expirationInfo?.badge}
                    </div>
                  ) : (
                    <span className="text-muted">-</span>
                  )}
                </Col>
              </Row>

              {lot.dateExpiration && expirationInfo && (
                <Row className="mb-3">
                  <Col sm={5}>
                    <strong>{t('lot.joursRestants')}:</strong>
                  </Col>
                  <Col sm={7}>
                    <span className={`fw-bold ${
                      expirationInfo.status === 'expired' ? 'text-danger' :
                      expirationInfo.status === 'critical' ? 'text-warning' :
                      expirationInfo.status === 'warning' ? 'text-info' : 'text-success'
                    }`}>
                      {expirationInfo.status === 'expired' ? 
                        t('lot.expiredSince', { days: expirationInfo.days }) :
                        t('lot.daysRemaining', { days: expirationInfo.days })
                      }
                    </span>
                  </Col>
                </Row>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Informations quantités */}
      <Row className="mb-4">
        <Col md={12}>
          <Card>
            <CardHeader className="bg-success text-white">
              <h5 className="mb-0">
                <FaChartLine className="me-2" />
                {t('lot.informationsQuantites')}
              </h5>
            </CardHeader>
            <CardBody>
              <Row className="mb-4">
                <Col md={3}>
                  <div className="text-center">
                    <div className="text-muted small">{t('lot.quantiteInitiale')}</div>
                    <div className="h3 text-primary">
                      {lot.quantiteInitiale}
                      <small className="text-muted ms-1">{lot.article?.unite || 'U'}</small>
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center">
                    <div className="text-muted small">{t('lot.quantiteDisponible')}</div>
                    <div className={`h3 ${lot.quantiteDisponible <= 0 ? 'text-danger' : 'text-success'}`}>
                      {lot.quantiteDisponible}
                      <small className="text-muted ms-1">{lot.article?.unite || 'U'}</small>
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center">
                    <div className="text-muted small">{t('lot.quantiteUtilisee')}</div>
                    <div className="h3 text-warning">
                      {(lot.quantiteInitiale - lot.quantiteDisponible).toFixed(3)}
                      <small className="text-muted ms-1">{lot.article?.unite || 'U'}</small>
                    </div>
                  </div>
                </Col>
                <Col md={3}>
                  <div className="text-center">
                    <div className="text-muted small">{t('lot.pourcentageUtilise')}</div>
                    <div className="h3 text-info">
                      {utilisationPercentage.toFixed(1)}%
                    </div>
                  </div>
                </Col>
              </Row>

              {/* Barre de progression */}
              <div className="mb-3">
                <div className="d-flex justify-content-between mb-1">
                  <small className="text-muted">{t('lot.utilisationLot')}</small>
                  <small className="text-muted">{utilisationPercentage.toFixed(1)}%</small>
                </div>
                <Progress
                  value={utilisationPercentage}
                  color={
                    utilisationPercentage >= 90 ? 'danger' :
                    utilisationPercentage >= 70 ? 'warning' :
                    utilisationPercentage >= 50 ? 'info' : 'success'
                  }
                  style={{ height: '10px' }}
                />
              </div>

              {/* Alertes */}
              {lot.quantiteDisponible <= 0 && (
                <Alert color="danger">
                  <FaExclamationTriangle className="me-2" />
                  {t('lot.lotEpuise')}
                </Alert>
              )}

              {expirationInfo?.status === 'expired' && (
                <Alert color="danger">
                  <FaExclamationTriangle className="me-2" />
                  {t('lot.lotExpire', { days: expirationInfo.days })}
                </Alert>
              )}

              {expirationInfo?.status === 'critical' && (
                <Alert color="warning">
                  <FaExclamationTriangle className="me-2" />
                  {t('lot.lotExpireBientot', { days: expirationInfo.days })}
                </Alert>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Historique des mouvements */}
      <Row>
        <Col md={12}>
          <Card>
            <CardHeader className="bg-secondary text-white">
              <h5 className="mb-0">
                <FaHistory className="me-2" />
                {t('lot.historiqueMouvements')}
              </h5>
            </CardHeader>
            <CardBody>
              {loadingMouvements ? (
                <div className="text-center py-4">
                  <Spinner color="primary" />
                  <div className="mt-2 text-muted">{t('common.loading')}</div>
                </div>
              ) : mouvements && mouvements.length > 0 ? (
                <div className="table-responsive">
                  <Table striped bordered hover size="sm">
                    <thead className="table-light">
                      <tr>
                        <th>{t('mouvement.date')}</th>
                        <th>{t('mouvement.type')}</th>
                        <th>{t('mouvement.quantite')}</th>
                        <th>{t('mouvement.quantiteApres')}</th>
                        <th>{t('mouvement.motif')}</th>
                        <th>{t('mouvement.utilisateur')}</th>
                        <th>{t('mouvement.reference')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mouvements.map((mouvement, index) => (
                        <tr key={index}>
                          <td>
                            {new Date(mouvement.dateMouvement).toLocaleDateString('fr-FR')}
                            <br />
                            <small className="text-muted">
                              {new Date(mouvement.dateMouvement).toLocaleTimeString('fr-FR')}
                            </small>
                          </td>
                          <td>
                            {getMouvementTypeBadge(mouvement.typeMouvement)}
                          </td>
                          <td className={`text-center fw-bold ${
                            mouvement.typeMouvement === 'ENTREE' ? 'text-success' : 'text-danger'
                          }`}>
                            {mouvement.typeMouvement === 'ENTREE' ? '+' : '-'}
                            {Math.abs(mouvement.quantite)}
                            <small className="text-muted ms-1">{lot.article?.unite || 'U'}</small>
                          </td>
                          <td className="text-center">
                            {mouvement.quantiteApres}
                            <small className="text-muted ms-1">{lot.article?.unite || 'U'}</small>
                          </td>
                          <td>{mouvement.motif || '-'}</td>
                          <td>{mouvement.utilisateur || '-'}</td>
                          <td>
                            {mouvement.referenceDocument ? (
                              <code className="bg-light p-1 rounded">
                                {mouvement.referenceDocument}
                              </code>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-4 text-muted">
                  <FaHistory size={48} className="mb-3" />
                  <div>{t('lot.aucunMouvement')}</div>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default LotDetails;

